import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  ParkingGateGroup,
  ParkingGateGroupDto,
  ParkingGateOption,
} from '../../../../../core/models/system-settings/parking-master-data.model';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-parking-gate-group',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-parking-gate-group.component.html',
  styleUrl: './modal-parking-gate-group.component.css',
})
export class ModalParkingGateGroupComponent implements OnInit {
  @Input() itemId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;
  formData!: ParkingGateGroup;
  gateOptions: ParkingGateOption[] = [];
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean { return this.itemId > 0; }

  ngOnInit(): void {
    this.formData = this.mapper.createEmptyGateGroup(this.siteId || null, this.siteName);
    const companyId = Number(this.cookieService.get('company_id') || 1);
    this.formData.companyId = companyId > 0 ? companyId : 1;
    this.loadGateOptions();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void { this.close.emit(); }

  isGateSelected(gateId: number): boolean {
    return this.formData.gateIds.includes(gateId);
  }

  toggleGate(gateId: number, checked: boolean): void {
    const set = new Set(this.formData.gateIds);
    if (checked) set.add(gateId);
    else set.delete(gateId);
    this.formData.gateIds = Array.from(set).sort((a, b) => a - b);
  }

  onSave(): void {
    if (!this.formData.name?.trim()) {
      this.toast.error('กรุณาระบุชื่อกลุ่มประตู');
      return;
    }
    if (!this.formData.siteId) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }

    const userId = Number(this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.router.url) || 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = this.mapper.mapGateGroupPayload(this.formData, userId, menuId);
    const request$ = this.isEdit
      ? this.api.updateParkingGateGroup(this.itemId, payload)
      : this.api.insertParkingGateGroup(payload);

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        const mapped = this.mapper.mapActionResponse(res);
        this.isSaving.set(false);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || 'บันทึกสำเร็จ');
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadGateOptions(): void {
    if (!this.siteId) {
      this.gateOptions = [];
      return;
    }
    this.api.getParkingGatesForGroupOptions(this.siteId).subscribe({
      next: (res) => { this.gateOptions = this.mapper.mapGateOptions(res); },
      error: () => { this.gateOptions = []; },
    });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getParkingGateGroupById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapGateGroup(res as ParkingGateGroupDto);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.onClose();
      },
    });
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
